import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../lib/api';
import { AlertTriangle, ArrowLeft, Crown, RefreshCcw, Search, SlidersHorizontal } from 'lucide-react';

const STATUS_OPTIONS = [
  ['all', 'All paid users'],
  ['active', 'Active subscribers'],
  ['expired', 'Expired subscribers'],
  ['cancelled', 'Cancelled subscribers'],
  ['lifetime', 'Lifetime users'],
];

const PLAN_OPTIONS = [
  ['all', 'All plans'],
  ['platinum', 'Platinum only'],
  ['immortal', 'Immortal only'],
  ['radiant', 'Radiant only'],
];

const SORT_OPTIONS = [
  ['newest_subscription', 'Newest subscription first'],
  ['expiring_soon', 'Expiring soon first'],
  ['highest_tier', 'Highest tier first'],
  ['accounts_desc', 'Most accounts added'],
  ['recent_activity', 'Recently active users'],
];

const statusClass = (status) => {
  if (status === 'active') return 'border-cyan-400/50 text-cyan-300 bg-cyan-400/10';
  if (status === 'lifetime') return 'border-yellow-400/50 text-yellow-200 bg-yellow-500/10';
  if (status === 'cancelled') return 'border-red-500/50 text-red-300 bg-red-500/10';
  return 'border-orange-400/50 text-orange-300 bg-orange-500/10';
};

const planClass = (plan) => {
  if (plan === 'radiant') return 'border-yellow-400/60 text-yellow-200 bg-yellow-500/10';
  if (plan === 'immortal') return 'border-orange-400/60 text-orange-300 bg-orange-500/10';
  if (plan === 'platinum') return 'border-cyan-400/60 text-cyan-300 bg-cyan-400/10';
  return 'border-zinc-700 text-zinc-400';
};

const dateText = (value) => value ? new Date(value).toLocaleDateString() : 'N/A';
const dateTimeText = (value) => value ? new Date(value).toLocaleString() : 'N/A';

export default function AdminPaidSubscribers() {
  const [subscribers, setSubscribers] = useState([]);
  const [meta, setMeta] = useState({ currentPage: 1, totalPages: 1, totalSubscribers: 0, limit: 20 });
  const [filters, setFilters] = useState({ search: '', status: 'all', plan: 'all', sort: 'newest_subscription' });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/paid-subscribers', {
        params: { ...filters, page, limit: 20 },
      });
      setSubscribers(data.subscribers || []);
      setMeta({
        currentPage: data.currentPage || 1,
        totalPages: data.totalPages || 1,
        totalSubscribers: data.totalSubscribers || 0,
        limit: data.limit || 20,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load paid subscribers.');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchSubscribers(); }, [fetchSubscribers]);

  const updateFilter = (key, value) => {
    setFilters(cur => ({ ...cur, [key]: value }));
    setPage(1);
  };

  const applyFilters = (e) => {
    e.preventDefault();
    if (page === 1) fetchSubscribers();
    else setPage(1);
  };

  const start = meta.totalSubscribers ? ((meta.currentPage - 1) * meta.limit) + 1 : 0;
  const end = meta.totalSubscribers ? Math.min(meta.currentPage * meta.limit, meta.totalSubscribers) : 0;

  return (
    <Layout hideFooter>
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-cyan-300 mb-1">// subscription command</div>
            <h1 className="font-mono-display text-3xl text-white flex items-center gap-3">
              <Crown className="w-7 h-7 text-yellow-300" />
              Paid Subscribers
            </h1>
            <p className="text-xs text-zinc-500 mt-2">Admin-only view of active and historical paid subscription users.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchSubscribers} disabled={loading} className="btn-ghost disabled:opacity-50">
              <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <Link to="/secure-portal-9x47/console" className="btn-ghost">
              <ArrowLeft className="w-3.5 h-3.5" />
              Admin
            </Link>
          </div>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded p-3">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            {error}
          </div>
        )}

        <form onSubmit={applyFilters} className="neon-card rounded-lg p-4 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <SlidersHorizontal className="w-4 h-4 text-cyan-300" />
            <div className="text-[10px] uppercase tracking-widest text-cyan-300">Subscriber Filters</div>
          </div>
          <div className="grid md:grid-cols-5 gap-3">
            <label className="block md:col-span-2">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Search by email/name</span>
              <div className="mt-1 flex items-center gap-2 bg-black/40 border border-zinc-800 focus-within:border-orange-400/70 rounded px-3 py-2">
                <Search className="w-4 h-4 text-zinc-500" />
                <input value={filters.search} onChange={(e) => updateFilter('search', e.target.value)} placeholder="email or username" className="bg-transparent flex-1 min-w-0 outline-none text-sm text-white placeholder:text-zinc-600" />
              </div>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Status</span>
              <select value={filters.status} onChange={(e) => updateFilter('status', e.target.value)} className="mt-1 w-full bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm text-white">
                {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Plan</span>
              <select value={filters.plan} onChange={(e) => updateFilter('plan', e.target.value)} className="mt-1 w-full bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm text-white">
                {PLAN_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Sort</span>
              <select value={filters.sort} onChange={(e) => updateFilter('sort', e.target.value)} className="mt-1 w-full bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm text-white">
                {SORT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
          </div>
        </form>

        <div className="neon-card rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-zinc-500 tabular">Showing {start}-{end} of {meta.totalSubscribers}</div>
            <div className="text-xs text-zinc-500 tabular">Page {meta.currentPage} of {meta.totalPages}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="text-left py-3 px-4">User</th>
                  <th className="text-left py-3 px-4">Plan</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Subscription</th>
                  <th className="text-left py-3 px-4">Accounts</th>
                  <th className="text-left py-3 px-4">Last Login</th>
                  <th className="text-left py-3 px-4">Payment</th>
                  <th className="text-left py-3 px-4">Created</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="py-8 text-center text-zinc-500 font-mono-display">Loading subscribers...</td></tr>
                )}
                {!loading && subscribers.length === 0 && (
                  <tr><td colSpan={8} className="py-8 text-center text-zinc-500 font-mono-display">No paid subscribers found.</td></tr>
                )}
                {!loading && subscribers.map(sub => (
                  <tr key={sub.id} className="border-t border-zinc-800">
                    <td className="py-3 px-4">
                      <div className="text-white font-mono-display">{sub.username || 'Unknown'}</div>
                      <div className="text-zinc-500 text-xs">{sub.email || 'No email'}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-1 items-start">
                        <span className={`px-2 py-0.5 rounded border text-[10px] uppercase tracking-widest ${planClass(sub.currentPlan)}`}>{sub.currentPlan || 'free'}</span>
                        {sub.paidPlan && sub.paidPlan !== sub.currentPlan && (
                          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Last paid: {sub.paidPlan}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 rounded border text-[10px] uppercase tracking-widest ${statusClass(sub.subscriptionStatus)}`}>{sub.subscriptionStatus}</span>
                    </td>
                    <td className="py-3 px-4 text-xs text-zinc-300">
                      <div>Start: {dateText(sub.subscriptionStartedAt)}</div>
                      <div>End: {dateText(sub.subscriptionExpiresAt)}</div>
                      <div className="text-zinc-500">Days: {sub.daysRemaining === null || sub.daysRemaining === undefined ? 'N/A' : sub.daysRemaining}</div>
                    </td>
                    <td className="py-3 px-4 text-white tabular">{sub.totalAccountsAdded || 0}</td>
                    <td className="py-3 px-4 text-zinc-300 text-xs">{dateTimeText(sub.lastLoginAt)}</td>
                    <td className="py-3 px-4 text-xs text-zinc-300">
                      {sub.payment ? (
                        <>
                          <div className="uppercase text-cyan-300">{sub.payment.status || 'unknown'}</div>
                          <div>{sub.payment.method || 'N/A'} // {sub.payment.amount ?? 'N/A'} PKR</div>
                        </>
                      ) : 'N/A'}
                    </td>
                    <td className="py-3 px-4 text-zinc-300 text-xs">{dateText(sub.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={meta.currentPage <= 1 || loading} className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
            <div className="text-xs text-zinc-400 tabular">Page {meta.currentPage} of {meta.totalPages}</div>
            <button onClick={() => setPage(p => Math.min(meta.totalPages, p + 1))} disabled={meta.currentPage >= meta.totalPages || loading} className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
